import { useCallback, useState } from 'react'
import download from 'downloadjs'

import { isCsrfFailure, refreshCsrfToken } from 'utils/csrfTokenStore'
import { wrapCSVTextToBlob, xhrExtractFileName } from 'utils/helpers'
import { getDefaultHeaders } from 'utils/httpUtils'

const CSRF_TOKEN_HEADER = 'X-CSRFToken'
const ABSOLUTE_URL_PATTERN = /^[a-z][a-z\d+.-]*:/i

/**
 * Hook to handle download actions with loading state
 * @returns {{
 *   handleDownloadClick: (args: {
 *     e: React.MouseEvent,
 *     isDisabled?: boolean,
 *     downloadIt?: string | boolean,
 *     downloadObj?: {
 *       method?: string,
 *       url: string,
 *       body?: object,
 *       csv?: boolean
 *     },
 *     href?: string,
 *   }) => void
 * }}
 */
const useDownloadHandler = () => {
  const [isLoading, setIsLoading] = useState(false)

  const handleDownloadClick = useCallback(({ e, isDisabled, downloadIt, downloadObj, href }) => {
    if (isDisabled) return

    if (downloadIt) {
      e.preventDefault()
      download(
        href && !ABSOLUTE_URL_PATTERN.test(href) && !href.startsWith('//')
          ? `${API_BASE_URL || ''}${href.startsWith('/') ? href : `/${href}`}`
          : href,
        typeof downloadIt === 'string' ? downloadIt : undefined
      )
      return
    }

    if (downloadObj) {
      e.preventDefault()
      setIsLoading(true)

      const downloadUrl =
        downloadObj.url &&
        !ABSOLUTE_URL_PATTERN.test(downloadObj.url) &&
        !downloadObj.url.startsWith('//')
          ? `${API_BASE_URL || ''}${downloadObj.url.startsWith('/') ? downloadObj.url : `/${downloadObj.url}`}`
          : downloadObj.url

      const send = (csrfToken, csrfRetried) => {
        const x = new XMLHttpRequest()
        x.open(downloadObj.method || 'GET', downloadUrl, true)
        x.setRequestHeader(CSRF_TOKEN_HEADER, csrfToken)
        x.setRequestHeader('Content-Type', 'application/json;charset=UTF-8')
        x.responseType = 'blob'
        x.withCredentials = true

        const finish = (ev) => {
          const res = downloadObj.csv ? wrapCSVTextToBlob(x.response) : x.response

          download(res, xhrExtractFileName(x), x.response.type)
          setIsLoading(false)
        }

        x.onload = function onLoadDownload(ev) {
          if (!csrfRetried && x.status === 403) {
            // blob response: read the body text to detect a stale CSRF token, then retry once
            x.response.text().then(
              (text) => {
                let detail
                try {
                  detail = JSON.parse(text)?.detail ?? text
                } catch {
                  detail = text
                }
                if (isCsrfFailure(403, detail)) {
                  refreshCsrfToken().then(
                    (token) => send(token, true),
                    () => setIsLoading(false)
                  )
                } else {
                  finish(ev)
                }
              },
              () => finish(ev)
            )
            return
          }
          finish(ev)
        }

        x.onerror = () => {
          setIsLoading(false)
        }

        x.send(downloadObj.body ? JSON.stringify(downloadObj.body) : null)
      }

      send(getDefaultHeaders()[CSRF_TOKEN_HEADER], false)
    }
  }, [])

  return { isLoading, handleDownloadClick }
}

export default useDownloadHandler
